import {
  ledgerSummary,
  loadAllContributions,
  openRefundModal,
  type ContributionWithRequest,
} from "../lib/ledger";
import { h } from "../lib/dom";
import { formatCents, formatDateTime } from "../lib/format";

export function renderLedger(): HTMLElement {
  const summaryEl = h("div", { class: "ledger-summary" });
  const listEl = h("div", { class: "ledger-table-wrap" }, h("p", { class: "rows-empty" }, "Loading ledger…"));

  const refresh = () => void load(summaryEl, listEl);

  void load(summaryEl, listEl);

  return h(
    "main",
    { class: "page" },
    h(
      "header",
      { class: "page-head" },
      h(
        "div",
        {},
        h("p", { class: "eyebrow" }, "Accounting"),
        h("h1", { class: "page-title" }, "Contribution ledger"),
      ),
      h(
        "button",
        {
          class: "btn btn-ghost btn-sm",
          type: "button",
          onclick: refresh,
        },
        "Refresh",
      ),
    ),
    h(
      "p",
      { class: "section-hint" },
      "All reader contributions recorded when Stripe checkout completes. Record refunds here after processing them in Stripe.",
    ),
    summaryEl,
    listEl,
  );
}

async function load(summaryEl: HTMLElement, listEl: HTMLElement): Promise<void> {
  listEl.replaceChildren(h("p", { class: "rows-empty" }, "Loading ledger…"));
  const { rows, error } = await loadAllContributions();

  if (error) {
    summaryEl.replaceChildren();
    listEl.replaceChildren(h("p", { class: "rows-error" }, `Could not load ledger: ${error}`));
    return;
  }

  const summary = ledgerSummary(rows);
  summaryEl.replaceChildren(
    h(
      "div",
      { class: "ledger-summary-grid" },
      summaryCard("Entries", String(summary.count)),
      summaryCard("Gross", formatCents(summary.totalCents)),
      summaryCard("Refunded", formatCents(summary.refundedCents)),
      summaryCard("Net", formatCents(summary.netCents)),
    ),
  );

  if (rows.length === 0) {
    listEl.replaceChildren(
      h("p", { class: "rows-empty" }, "No ledger entries yet. Paid submissions appear after checkout completes."),
    );
    return;
  }

  listEl.replaceChildren(
    h(
      "table",
      { class: "ledger-table" },
      h(
        "thead",
        {},
        h("tr", {}, ...["Date", "Amount", "Refund", "Net", "Submission", "Review", "Reference", ""].map((label) => h("th", { scope: "col" }, label))),
      ),
      h("tbody", {}, ...rows.map((row) => renderRow(row, () => void load(summaryEl, listEl)))),
    ),
  );
}

function summaryCard(label: string, value: string): HTMLElement {
  return h(
    "div",
    { class: "ledger-summary-card" },
    h("p", { class: "ledger-summary-label" }, label),
    h("p", { class: "ledger-summary-value" }, value),
  );
}

function renderRow(row: ContributionWithRequest, onUpdated: () => void): HTMLElement {
  const netCents = Math.max(0, row.amount_cents - row.refunded_cents);
  const submission = row.review_requests;
  const processorRef = row.processor_reference?.trim() || "—";

  return h(
    "tr",
    { class: row.refunded_cents > 0 ? "ledger-row is-refunded" : "ledger-row" },
    h("td", { class: "ledger-cell-date" }, formatDateTime(row.created_at)),
    h("td", { class: "ledger-cell-amount" }, formatCents(row.amount_cents)),
    h(
      "td",
      { class: "ledger-cell-refund" },
      row.refunded_cents > 0 ? formatCents(row.refunded_cents) : h("span", { class: "detail-muted" }, "—"),
    ),
    h("td", { class: "ledger-cell-net" }, formatCents(netCents)),
    h(
      "td",
      { class: "ledger-cell-submission" },
      submission
        ? h(
            "div",
            { class: "ledger-submission-cell" },
            h("span", { class: "ledger-submission-name" }, submission.restaurant_name),
            h("span", { class: "ledger-submission-meta" }, submission.requester_email),
            row.review_request_id
              ? h(
                  "button",
                  {
                    class: "detail-link-btn",
                    type: "button",
                    onclick: () => {
                      window.location.hash = "/requests";
                    },
                  },
                  "View submission",
                )
              : null,
          )
        : h("span", { class: "detail-muted" }, "—"),
    ),
    h(
      "td",
      { class: "ledger-cell-review" },
      row.review_id
        ? h(
            "button",
            {
              class: "detail-link-btn",
              type: "button",
              onclick: () => {
                window.location.hash = `/editor/${row.review_id}`;
              },
            },
            "Open review",
          )
        : h("span", { class: "detail-muted" }, "Not linked"),
    ),
    h(
      "td",
      { class: "ledger-cell-ref" },
      processorRef === "—"
        ? h("span", { class: "detail-muted" }, "—")
        : h("code", { class: "ledger-ref" }, processorRef),
    ),
    h(
      "td",
      { class: "ledger-cell-actions" },
      h(
        "button",
        {
          class: "btn btn-ghost btn-sm",
          type: "button",
          onclick: () => openRefundModal(row, onUpdated),
        },
        row.refunded_cents > 0 ? "Update" : "Refund",
      ),
    ),
  );
}
